import { useCallback, useEffect, useState } from "react";
import {
  AdEngineClientConfig,
  AdReportDoc,
  AdSwipeDoc,
  AdSwipeStatus,
  getAdEngineClientConfig,
  getLatestAdReport,
  listAdSwipesClient,
  updateAdSwipeStatus,
} from "../../../../../database/community/adEngine";

// One-shot loads, local to the Ads section (same reasoning as the content
// engine hook on Home: most communities have no ad engine yet, so these
// reads should not run on every studio mount). Status updates write through
// and patch local state so triage feels instant.

export type AdEngineState = {
  loading: boolean;
  config: AdEngineClientConfig | null;
  swipes: AdSwipeDoc[];
  report: AdReportDoc | null;
  setStatus: (id: string, status: AdSwipeStatus) => Promise<void>;
};

export const useAdEngine = (communityId: string | undefined): AdEngineState => {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<AdEngineClientConfig | null>(null);
  const [swipes, setSwipes] = useState<AdSwipeDoc[]>([]);
  const [report, setReport] = useState<AdReportDoc | null>(null);

  useEffect(() => {
    if (!communityId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const [cfg, rows, rep] = await Promise.all([
        getAdEngineClientConfig(communityId).catch(() => null),
        listAdSwipesClient(communityId).catch(() => [] as AdSwipeDoc[]),
        getLatestAdReport(communityId).catch(() => null),
      ]);
      if (cancelled) return;
      setConfig(cfg);
      setSwipes(rows);
      setReport(rep);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [communityId]);

  const setStatus = useCallback(
    async (id: string, status: AdSwipeStatus) => {
      if (!communityId) return;
      await updateAdSwipeStatus(communityId, id, { status });
      setSwipes((prev) =>
        prev.map((s) => (s.id === id ? { ...s, status } : s))
      );
    },
    [communityId]
  );

  return { loading, config, swipes, report, setStatus };
};
